import { useEffect } from 'react';

/** Tempo mínimo em background para que o resume dispare reload. */
const LIMITE_BACKGROUND_MS = 60_000;

/** Detecta se o app está rodando como PWA instalado (standalone). */
function isStandalonePwa(): boolean {
  if (typeof window === 'undefined') return false;
  const mql = window.matchMedia?.('(display-mode: standalone)');
  // iOS Safari expõe navigator.standalone em vez de display-mode
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return Boolean(mql?.matches) || iosStandalone;
}

/**
 * usePwaResumeReload
 *
 * No PWA admin/tenant, recarrega a página quando o app volta do background
 * após ficar oculto por mais de LIMITE_BACKGROUND_MS. Garante dados frescos
 * e reconexão do Realtime sem o usuário precisar matar o app.
 *
 * Só ativa em modo standalone — em aba normal de navegador não recarrega
 * (evita interromper uma edição em andamento). Não deve ser montado no
 * Player (tem watchdog próprio) nem no Jukebox (gerencia canais no visibilitychange).
 */
export function usePwaResumeReload(): void {
  useEffect(() => {
    if (!isStandalonePwa()) return;

    let hiddenAt: number | null = null;

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }

      // visible
      if (hiddenAt !== null && Date.now() - hiddenAt > LIMITE_BACKGROUND_MS) {
        window.location.reload();
      }
      hiddenAt = null;
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);
}
